import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { apiPyAxios } from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import './login.css';

const SHOW_EMAIL_LOGIN = true;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Google SSO state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Email-OTP state
  const [otpStep, setOtpStep] = useState('email'); // 'email' | 'code'
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) navigate('/home', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  // ── Google SSO ──────────────────────────────────────────────────────────────
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const res = await apiPyAxios.post('auth/google/', {
        id_token: credentialResponse.credential,
      });
      login(res.data);
      navigate('/home', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Sign-in failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed. Please try again.');
  };

  // ── Email-OTP ───────────────────────────────────────────────────────────────
  const startCooldown = (seconds) => {
    setResendCooldown(seconds);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setOtpError('');
    const email = otpEmail.trim();
    if (!email) { setOtpError('Please enter your email address.'); return; }
    setOtpLoading(true);
    try {
      await apiPyAxios.post('auth/email-otp/request/', { email });
      setOtpStep('code');
      startCooldown(60);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to send code. Please try again.';
      setOtpError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setOtpError('');
    if (!otpCode.trim()) { setOtpError('Please enter the 6-digit code.'); return; }
    setOtpLoading(true);
    try {
      const res = await apiPyAxios.post('auth/email-otp/verify/', {
        email: otpEmail.trim(),
        code: otpCode.trim(),
      });
      login(res.data);
      navigate('/home', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Verification failed. Please try again.';
      setOtpError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || otpLoading) return;
    setOtpError('');
    setOtpCode('');
    setOtpLoading(true);
    try {
      await apiPyAxios.post('auth/email-otp/request/', { email: otpEmail.trim() });
      startCooldown(60);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to resend code.';
      setOtpError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setOtpStep('email');
    setOtpCode('');
    setOtpError('');
    clearInterval(cooldownRef.current);
    setResendCooldown(0);
  };

  const pageBg = {
    backgroundImage:
      "linear-gradient(rgba(5,5,20,0.68), rgba(5,5,20,0.68)), url('/images/hero-slide-1.jpg')",
  };

  return (
    <div className="login-page" style={pageBg}>
      <div className="login-card">
        <div className="login-logo">
          <img src="/images/mahabharat-logo.svg" alt="Mahabharat Museum" className="login-logo-img" />
          <span className="login-logo-text">Mahabharat Museum</span>
        </div>

        <h1 className="login-heading">Welcome</h1>
        <p className="login-subheading">Sign in to access your profile &amp; receive notifications</p>

        {error && (
          <div className="login-error" role="alert">{error}</div>
        )}

        {/* Google SSO */}
        {loading ? (
          <div className="login-loading">
            <span className="login-spinner" aria-hidden="true" />
            <span>Signing in…</span>
          </div>
        ) : (
          <div className="login-google-btn-wrap">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="filled_black"
              shape="rectangular"
              size="large"
              text="signin_with"
              width="320"
            />
          </div>
        )}

        {SHOW_EMAIL_LOGIN && (
          <>
            <div className="login-divider"><span>or</span></div>

            {/* Email-OTP */}
            <div className="login-otp-section">
              {otpError && (
                <div className="login-error" role="alert">{otpError}</div>
              )}

              {otpStep === 'email' ? (
                <form onSubmit={handleSendCode} noValidate>
                  <input
                    className="login-input"
                    type="email"
                    placeholder="Your email address"
                    value={otpEmail}
                    onChange={(e) => { setOtpEmail(e.target.value); setOtpError(''); }}
                    autoComplete="email"
                    disabled={otpLoading}
                  />
                  <button type="submit" className="login-otp-btn" disabled={otpLoading}>
                    {otpLoading
                      ? <><span className="login-spinner" aria-hidden="true" /> Sending…</>
                      : 'Send Code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyCode} noValidate>
                  <p className="login-otp-hint">
                    Code sent to <strong>{otpEmail}</strong>
                  </p>
                  <input
                    className="login-input login-input--code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '')); setOtpError(''); }}
                    autoComplete="one-time-code"
                    disabled={otpLoading}
                    autoFocus
                  />
                  <button type="submit" className="login-otp-btn" disabled={otpLoading}>
                    {otpLoading
                      ? <><span className="login-spinner" aria-hidden="true" /> Verifying…</>
                      : 'Verify & Login'}
                  </button>
                  <div className="login-otp-actions">
                    <button
                      type="button"
                      className="login-otp-link"
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || otpLoading}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                    <button
                      type="button"
                      className="login-otp-link"
                      onClick={handleBackToEmail}
                      disabled={otpLoading}
                    >
                      Change email
                    </button>
                  </div>
                </form>
              )}
            </div>
          </>
        )}

        <p className="login-footer-note">
          By signing in you agree to our terms of use.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
